import { Checkbox, Divider, FormControlLabel, FormGroup, Grid } from "@mui/material";
import { useCallback, useContext, useState } from "react";
import ProposalContext from "modules/proposals/providers/ProposalContextProvider";
import useGetOrganization from "hooks/organization/useGetOrganization";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiTypography from "components/base/UiTypography";
import useUpdateProposalSalesTaxes from "hooks/proposals/useUpdateProposalSalesTaxes";

type DraftSalesTaxProps = {
  onClose: () => void;
};

export default function DraftSalesTax({ onClose }: DraftSalesTaxProps) {
  const { proposal } = useContext(ProposalContext);
  const { data } = useGetOrganization();
  const [mutation, { isSuccess }] = useUpdateProposalSalesTaxes();

  const [checked, setChecked] = useState<Record<string, { checked: boolean } | undefined>>(() => {
    return proposal.salesTaxes.reduce(
      (acc, val) => ({
        ...acc,
        [val.organizationSalesTaxId]: { checked: true },
      }),
      {}
    );
  });

  const handleChecked = useCallback((id: string, isChecked: boolean) => {
    setChecked((prev) => ({ ...prev, [id]: { checked: isChecked } }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const ids = Object.entries(checked)
        .filter(([, val]) => val?.checked)
        .map(([key]) => key);

      const result = await mutation({
        variables: { input: { id: proposal.id, salesTaxes: ids } },
      });
      if (isSuccess(result.data)) onClose();
    },
    [mutation, checked, proposal.id, isSuccess, onClose]
  );

  return (
    <MDBox p="24px" component="form" onSubmit={handleSubmit}>
      <MDBox display="flex" flexDirection="column" gap="4px">
        {data?.salesTaxes?.map((tax) => (
          <FormGroup key={tax.id}>
            <Grid container alignItems="center">
              <Grid item xs={8}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={checked[tax.id]?.checked ?? false}
                      onChange={(e) => handleChecked(tax.id, e.target.checked)}
                    />
                  }
                  label={
                    <UiTypography uiVariant="body" sx={{ color: "#212936" }}>
                      {tax.name}
                    </UiTypography>
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <UiTypography uiVariant="body" sx={{ textAlign: "right", color: "#212936" }}>
                  {tax.percentage}%
                </UiTypography>
              </Grid>
            </Grid>
            <Divider sx={{ my: "4px" }} />
          </FormGroup>
        ))}
      </MDBox>

      <MDBox display="flex" justifyContent="flex-end" mt="16px">
        <UiButton uiVariant="primary-green" type="submit">
          Save
        </UiButton>
      </MDBox>
    </MDBox>
  );
}
